import { useEffect, useMemo, useRef, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import NodeChartTooltip from './NodeChartTooltip';
import { AXIS, formatShare, resolveCodeColor } from './nodeChartTheme';
import { trackEvent } from '../../lib/usageEvents';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Кольцевая диаграмма для взаимоисключающего выбора с небольшим числом значений.
 *
 * Радиус считается по фактическому размеру контейнера, а не берётся константой: карточка
 * живёт в двухколоночной сетке, и на узком экране кольцо фиксированного радиуса вылезало
 * за пределы области.
 *
 * Легенда своя, а не `<Legend>` из recharts: библиотека резервирует под неё высоту внутри
 * области графика, из-за чего кольцо съезжает вверх, а поведение между версиями recharts
 * менялось. Обычный flex-список под графиком предсказуем и переносится по строкам.
 */
const NodeDonutChart = ({ spec, rows, height, onSelect }) => {
  const ringRef = useRef(null);
  const [ringSize, setRingSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ringRef.current;
    if (!element) {
      return undefined;
    }

    const updateSize = () =>
      setRingSize({ width: element.clientWidth, height: element.clientHeight });

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.contentRect;
      setRingSize({
        width: rect?.width ?? element.clientWidth,
        height: rect?.height ?? element.clientHeight
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const data = useMemo(() => {
    const usedColors = new Set();
    return rows
      .filter((row) => row.calls > 0)
      .map((row, index) => ({
        ...row,
        color: resolveCodeColor(row.code, index, {
          tone: spec.tone,
          colorRoles: spec.colorRoles,
          usedColors
        })
      }));
  }, [rows, spec.tone, spec.colorRoles]);

  const radius = useMemo(() => {
    const width = ringSize.width || 320;
    const boxHeight = ringSize.height || 200;
    const outer = clamp(Math.floor(Math.min((width - 48) / 2, boxHeight / 2 - 8)), 34, 92);
    return { outer, inner: Math.round(outer * 0.6) };
  }, [ringSize]);

  if (data.length === 0) {
    return null;
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col" style={{ minHeight: height }}>
      <div ref={ringRef} className="min-h-0 w-full flex-1">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={140}>
          <PieChart margin={{ top: 4, right: 0, bottom: 4, left: 0 }}>
            <Pie
              data={data}
              dataKey="calls"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={radius.inner}
              outerRadius={radius.outer}
              paddingAngle={2}
              isAnimationActive={false}
              cursor={onSelect ? 'pointer' : 'default'}
              onClick={(entry) => {
                if (onSelect) {
                  trackEvent('nodes.donut.select');
                }
                onSelect?.(entry?.payload);
              }}
            >
              {data.map((row) => (
                <Cell key={row.code} fill={row.color} stroke="hsl(var(--card))" />
              ))}
            </Pie>
            <Tooltip content={<NodeChartTooltip />} wrapperStyle={AXIS.tooltipWrapperStyle} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs">
        {data.map((row) => (
          <li key={row.code} className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
            <span className="text-muted-foreground">
              {row.label} · {formatShare(row.share)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default NodeDonutChart;
